import { useState, useRef } from 'react';
import { Trash, ArrowsLeftRight, VideoCamera } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const monoFont = "'IBM Plex Mono', monospace";

export interface GalleryRowData {
  id: string;
  leftUrl: string;
  rightUrl: string;
  title: string | null;
  sortOrder: number;
  videoUrl?: string | null;
  videoDisplayMode?: string | null;
  /** Approval-only fields */
  approveCount?: number;
  rejectCount?: number;
}

interface GalleryRowCardProps {
  row: GalleryRowData;
  index: number;
  mode: 'gallery' | 'approval';

  // Drag
  isDragging?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;

  // Image slots
  renderLeftSlot?: React.ReactNode;
  renderRightSlot?: React.ReactNode;

  // Swap
  onSwapSides?: () => void;

  // Title/Caption
  onSaveTitle?: (value: string) => void;
  onSaveVideoUrl?: (value: string) => void;

  // Actions
  canMerge?: boolean;
  canSplit?: boolean;
  onMerge?: () => void;
  onSplit?: () => void;
  onDeleteRow?: () => void;
  onDeleteLeft?: () => void;
  onDeleteRight?: () => void;
}

export function GalleryRowCard({
  row,
  index,
  mode,
  isDragging,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  renderLeftSlot,
  renderRightSlot,
  onSwapSides,
  onSaveTitle,
  onSaveVideoUrl,
  canMerge,
  canSplit,
  onMerge,
  onSplit,
  onDeleteRow,
  onDeleteLeft,
  onDeleteRight,
}: GalleryRowCardProps) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [editingVideo, setEditingVideo] = useState(false);
  const [videoValue, setVideoValue] = useState('');

  const hasLeft = Boolean(row.leftUrl?.trim());
  const hasRight = Boolean(row.rightUrl?.trim());
  const hasBoth = hasLeft && hasRight;
  const isSingle = (hasLeft ? 1 : 0) + (hasRight ? 1 : 0) === 1;
  const isVideoRow = Boolean(row.videoUrl);
  const titleLabel = mode === 'approval' ? '+ legenda' : '+ título';
  const lineLabel = isVideoRow ? '' : hasBoth ? '2 por linha' : '1 por linha';

  const startEditing = () => {
    setEditingTitle(true);
    setTitleValue(row.title || '');
  };

  const saveTitle = () => {
    onSaveTitle?.(titleValue.trim());
    setEditingTitle(false);
  };

  const startEditingVideo = () => {
    setEditingVideo(true);
    setVideoValue(row.videoUrl || '');
  };

  const saveVideo = () => {
    onSaveVideoUrl?.(videoValue.trim());
    setEditingVideo(false);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div
        onDragOver={onDragOver}
        onDrop={onDrop}
        className="flex items-center gap-3 p-2"
        style={{
          backgroundColor: isDragging ? 'rgba(247,246,242,0.06)' : 'rgba(247,246,242,0.02)',
          opacity: isDragging ? 0.5 : 1,
          transition: 'background-color 0.15s ease, opacity 0.15s ease',
        }}
      >
        {/* Drag handle */}
        <button
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          className="cursor-grab active:cursor-grabbing shrink-0"
          title="Reordenar linha"
          style={{ color: 'rgba(247,246,242,0.2)' }}
        >
          <span style={{ fontFamily: monoFont, fontSize: 14 }}>≡</span>
        </button>

        {/* Number */}
        <span style={{ fontFamily: monoFont, fontSize: 12, color: 'rgba(247,246,242,0.4)', width: 16, flexShrink: 0 }}>
          {index + 1}
        </span>

        {/* Thumbnails */}
        {isVideoRow ? (
          <div className="shrink-0 flex items-center justify-center" style={{ width: 64, height: 64, backgroundColor: 'rgba(247,246,242,0.04)', position: 'relative', overflow: 'hidden' }}>
            {hasLeft && <img src={row.leftUrl} alt="" className="w-full h-full object-cover" style={{ opacity: 0.6 }} />}
            <span style={{ position: 'absolute', fontSize: 20, color: 'rgba(247,246,242,0.7)' }}>▶</span>
          </div>
        ) : (
        <div className="flex items-center gap-0 shrink-0">
          {renderLeftSlot}

          {hasBoth && onSwapSides && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); onSwapSides(); }}
                  className="flex items-center justify-center mx-1"
                  style={{
                    color: 'rgba(247,246,242,0.2)',
                    cursor: 'pointer',
                    transition: 'color 0.15s ease',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(247,246,242,0.6)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(247,246,242,0.2)'; }}
                >
                  <ArrowsLeftRight size={16} weight="thin" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top"><span style={{ fontSize: 12, fontFamily: monoFont }}>Trocar posições</span></TooltipContent>
            </Tooltip>
          )}

          {renderRightSlot}
        </div>
        )}

        {/* Title / Caption */}
        <div className="flex-1 min-w-0">
          {editingTitle ? (
            <div className="flex gap-1 items-center">
              <input
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveTitle()}
                className="text-xs bg-transparent flex-1"
                style={{
                  fontFamily: monoFont,
                  fontWeight: 300,
                  padding: '4px 6px',
                  border: '0.5px solid rgba(247,246,242,0.15)',
                  color: 'rgba(247,246,242,0.85)',
                }}
                autoFocus
                placeholder={mode === 'approval' ? 'Legenda...' : 'Título...'}
              />
              <button
                onClick={saveTitle}
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  fontWeight: 300,
                  textTransform: 'uppercase',
                  padding: '4px 8px',
                  border: '0.5px solid rgba(247,246,242,0.15)',
                  color: 'rgba(247,246,242,0.7)',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                OK
              </button>
              <button
                onClick={() => setEditingTitle(false)}
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  fontWeight: 300,
                  textTransform: 'uppercase',
                  padding: '4px 8px',
                  color: 'rgba(247,246,242,0.4)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <span
                onClick={(e) => { e.stopPropagation(); startEditing(); }}
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  fontWeight: 300,
                  color: row.title ? 'rgba(247,246,242,0.6)' : 'rgba(247,246,242,0.35)',
                  cursor: 'pointer',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(247,246,242,0.9)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = row.title ? 'rgba(247,246,242,0.6)' : 'rgba(247,246,242,0.35)'; }}
              >
                {row.title || titleLabel}
              </span>
              <div className="flex gap-3 items-center" style={{ marginTop: 2 }}>
                <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.3)', textTransform: 'uppercase' }}>
                  {lineLabel}
                </span>
                {isVideoRow && row.videoDisplayMode && (
                  <span style={{
                    fontFamily: monoFont, fontSize: 12, textTransform: 'uppercase',
                    padding: '1px 6px', border: '0.5px solid rgba(247,246,242,0.15)',
                    color: 'rgba(247,246,242,0.5)',
                  }}>
                    {row.videoDisplayMode === 'background' ? 'FUNDO' : 'VIEWPORT'}
                  </span>
                )}
                {row.videoUrl && !isVideoRow && (
                  <span style={{ fontFamily: monoFont, fontSize: 12, color: 'rgba(100,180,255,0.7)', textTransform: 'uppercase' }}>
                    ▶ vídeo
                  </span>
                )}
                {mode === 'approval' && (
                  <>
                    <span style={{ fontFamily: monoFont, fontSize: 12, color: 'hsl(142 71% 45%)' }}>
                      ✓ {row.approveCount || 0}
                    </span>
                    <span style={{ fontFamily: monoFont, fontSize: 12, color: '#FF3F2D' }}>
                      ✕ {row.rejectCount || 0}
                    </span>
                  </>
                )}
              </div>
              {/* Video URL editor */}
              {editingVideo && (
                <div className="flex gap-1 items-center mt-1">
                  <input
                    value={videoValue}
                    onChange={(e) => setVideoValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveVideo()}
                    className="text-xs bg-transparent flex-1"
                    style={{
                      fontFamily: monoFont, fontWeight: 300, padding: '4px 6px',
                      border: '0.5px solid rgba(100,180,255,0.3)', color: 'rgba(247,246,242,0.85)',
                    }}
                    autoFocus
                    placeholder="https://youtube.com/watch?v=... ou https://vimeo.com/..."
                  />
                  <button onClick={saveVideo} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '4px 8px', border: '0.5px solid rgba(247,246,242,0.15)', color: 'rgba(247,246,242,0.7)', background: 'transparent', cursor: 'pointer' }}>OK</button>
                  <button onClick={() => setEditingVideo(false)} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, padding: '4px 8px', color: 'rgba(247,246,242,0.4)', background: 'transparent', border: 'none', cursor: 'pointer' }}>✕</button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-auto shrink-0">
          {/* Individual delete buttons for pairs */}
          {hasBoth && onDeleteLeft && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteLeft(); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'rgba(247,246,242,0.3)',
                    transition: 'color 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#FF3F2D'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(247,246,242,0.3)'; }}
                >
                  <Trash size={14} weight="thin" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top"><span style={{ fontSize: 12, fontFamily: monoFont }}>Apagar imagem esquerda</span></TooltipContent>
            </Tooltip>
          )}
          {hasBoth && onDeleteRight && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteRight(); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'rgba(247,246,242,0.3)',
                    transition: 'color 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#FF3F2D'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(247,246,242,0.3)'; }}
                >
                  <Trash size={14} weight="thin" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top"><span style={{ fontSize: 12, fontFamily: monoFont }}>Apagar imagem direita</span></TooltipContent>
            </Tooltip>
          )}

          {/* Single image delete */}
          {isSingle && onDeleteLeft && !hasBoth && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteLeft(); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'rgba(247,246,242,0.3)',
                    transition: 'color 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#FF3F2D'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(247,246,242,0.3)'; }}
                >
                  <Trash size={14} weight="thin" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top"><span style={{ fontSize: 12, fontFamily: monoFont }}>Apagar imagem</span></TooltipContent>
            </Tooltip>
          )}

          {/* Video embed - gallery only */}
          {mode === 'gallery' && onSaveVideoUrl && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); startEditingVideo(); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: row.videoUrl ? 'rgba(100,180,255,0.7)' : 'rgba(247,246,242,0.3)',
                    transition: 'color 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(100,180,255,1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = row.videoUrl ? 'rgba(100,180,255,0.7)' : 'rgba(247,246,242,0.3)'; }}
                >
                  <VideoCamera size={14} weight="thin" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top"><span style={{ fontSize: 12, fontFamily: monoFont }}>{row.videoUrl ? 'Editar vídeo' : 'Adicionar vídeo'}</span></TooltipContent>
            </Tooltip>
          )}

          {/* Merge / Split - gallery only */}
          {mode === 'gallery' && canSplit && onSplit && (
            <ActionTextButton label="SEPARAR" onClick={onSplit} />
          )}
          {mode === 'gallery' && canMerge && onMerge && (
            <ActionTextButton label="JUNTAR" onClick={onMerge} />
          )}

          {/* Delete row */}
          {onDeleteRow && (
            <ActionTextButton label="APAGAR" onClick={onDeleteRow} destructive />
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

function ActionTextButton({ label, onClick, destructive }: { label: string; onClick: () => void; destructive?: boolean }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        fontFamily: monoFont,
        fontSize: 12,
        fontWeight: 300,
        textTransform: 'uppercase',
        padding: '4px 8px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: destructive ? '#FF3F2D' : 'rgba(247,246,242,0.4)',
        opacity: destructive ? 0.7 : 1,
        transition: 'color 0.15s ease, opacity 0.15s ease',
      }}
      onMouseEnter={(e) => {
        if (destructive) {
          e.currentTarget.style.opacity = '1';
        } else {
          e.currentTarget.style.color = 'rgba(247,246,242,0.8)';
        }
      }}
      onMouseLeave={(e) => {
        if (destructive) {
          e.currentTarget.style.opacity = '0.7';
        } else {
          e.currentTarget.style.color = 'rgba(247,246,242,0.4)';
        }
      }}
    >
      {label}
    </button>
  );
}
